/* eslint-disable better-mutation/no-mutation */
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { MODAL_STATE } from 'tradera/constants/ui';
import { getInitialBadges, type UiState } from './initial-state';

const uiSlice = createSlice({
    name: 'ui',
    initialState: {} as UiState,
    reducers: {
        initialize: (_state, { payload }) => {
            return {
                ...payload
            };
        },
        toggleHeaderSearch: (state, { payload }) => {
            state.showHeaderSearch = payload;
        },

        openVideoModal: (state, { payload }) => {
            const {
                videoUrl,
                videoId,
                title,
                pageType,
                buttonLink,
                buttonText
            } = payload;
            state.videoModal = {
                ...state.videoModal,
                open: MODAL_STATE.OPEN,
                videoUrl,
                videoId,
                title,
                pageType,
                buttonLink,
                buttonText
            };
        },
        closeVideoModal: (state) => {
            state.videoModal.open = MODAL_STATE.CLOSED;
        },
        openNewMessageModal: (state, { payload }) => {
            state.newMessageModal = {
                open: MODAL_STATE.OPEN,
                title: payload.title,
                senderId: payload.senderId,
                recipientId: payload.recipientId,
                recipientAlias: payload.recipientAlias,
                itemId: payload.itemId,
                itemTitle: payload.itemTitle,
                itemImageUrl: payload.itemImageUrl,
                purchaseOrderId: payload.purchaseOrderId,
                itemSellerId: payload.itemSellerId
            };
        },
        closeNewMessageModal: (state) => {
            state.newMessageModal.open = MODAL_STATE.CLOSED;
        },
        openNotificationOptinModal: (state, { payload }) => {
            const { modalType, gaLabel, title, description, tagGroups } =
                payload;
            state.notificationOptinModal = {
                ...state.notificationOptinModal,
                open: MODAL_STATE.OPEN,
                modalType,
                gaLabel,
                title,
                description,
                tagGroups
            };
        },
        closeNotificationOptinModal: (state) => {
            state.notificationOptinModal.open = MODAL_STATE.CLOSED;
        },
        openModal: (state, { payload }) => {
            const { modalId, data } = payload;
            state.modals = {
                currentModal: modalId,
                currentModalData: data
            };
        },
        closeModal: (state) => {
            state.modals = {
                currentModal: null,
                currentModalData: null
            };
        },
        setSystemMessages: (state, { payload }) => {
            state.systemMessages = payload.messages;
            state.badges.messageNotificationCount = payload.notificationCount;
        },
        setSystemBadges: (state, { payload }) => {
            state.systemBadges = payload.badges;
            state.badges.badgeNotificationCount = payload.notificationCount;
        },
        openProfileMenu: (state, { payload }) => {
            state.profileMenu = {
                open: MODAL_STATE.OPEN,
                redirectUrl: payload || false
            };
        },
        closeProfileMenu: (state) => {
            state.profileMenu.open = MODAL_STATE.CLOSED;
        },
        openCategoryDropdownMenu: (state) => {
            state.categoryDropdownMenu.open = MODAL_STATE.OPEN;
        },
        closeCategoryDropdownMenu: (state) => {
            state.categoryDropdownMenu.open = MODAL_STATE.CLOSED;
        },
        openSearchForm: (state) => {
            state.showSearchForm = true;
        },
        closeSearchForm: (state) => {
            state.showSearchForm = false;
        },
        logOut: (state) => {
            state.badges = getInitialBadges();
        },
        openItemSettings: (state, { payload }) => {
            state.itemSettings = {
                open: MODAL_STATE.OPEN,
                item: payload
            };
        },
        closeItemSettings: (state) => {
            state.itemSettings = {
                open: MODAL_STATE.CLOSED,
                item: {}
            };
        },
        openShareModal: (state, { payload }) => {
            state.shareModal = {
                isOpen: true,
                settings: payload
            };
        },
        closeShareModal: (state) => {
            state.shareModal = {
                isOpen: false,
                settings: null
            };
        },
        openHelpSection: (state, { payload }) => {
            state.helpSection = {
                isOpen: true,
                settings: payload
            };
        },
        closeHelpSection: (state) => {
            state.helpSection = {
                isOpen: false,
                settings: null
            };
        },
        openWishListSideSheet: (state) => {
            state.wishListSideSheet.isOpen = true;
        },
        closeWishListSideSheet: (state) => {
            state.wishListSideSheet.isOpen = false;
        },
        openNotificationsSideSheet: (state) => {
            state.notificationsSideSheet.isOpen = true;
        },
        closeNotificationsSideSheet: (state) => {
            state.notificationsSideSheet.isOpen = false;
        },
        updateProductCommunications: (state, { payload }) => {
            state.productCommunications = payload;
        },
        showSaveSearchBanner: (
            state,
            { payload = true }: PayloadAction<boolean | undefined>
        ) => {
            state.showSaveSearchBanner = payload;
        },
        openCollectSsnModal: (state) => {
            state.collectSsnModal.isOpen = true;
        },
        closeCollectSsnModal: (state) => {
            state.collectSsnModal.isOpen = false;
        },
        openReferralModal: (state) => {
            state.referralModal.isOpen = true;
        },
        closeReferralModal: (state) => {
            state.referralModal.isOpen = false;
        }
    }
});

export const {
    initialize,
    toggleHeaderSearch,
    openVideoModal,
    closeVideoModal,
    openNewMessageModal,
    closeNewMessageModal,
    openNotificationOptinModal,
    closeNotificationOptinModal,
    setSystemMessages,
    setSystemBadges,
    openProfileMenu,
    closeProfileMenu,
    openCategoryDropdownMenu,
    closeCategoryDropdownMenu,
    openSearchForm,
    closeSearchForm,
    openItemSettings,
    closeItemSettings,
    logOut,
    openModal,
    closeModal,
    openShareModal,
    closeShareModal,
    openHelpSection,
    closeHelpSection,
    openWishListSideSheet,
    closeWishListSideSheet,
    openNotificationsSideSheet,
    closeNotificationsSideSheet,
    updateProductCommunications,
    showSaveSearchBanner,
    openCollectSsnModal,
    closeCollectSsnModal,
    openReferralModal,
    closeReferralModal
} = uiSlice.actions;

export const reducer = uiSlice.reducer;
